import { Controller } from "@hotwired/stimulus"

// Manages an admin table's column visibility + ordering.
//
// Extracted from the Funding Portal (QAN-513) so every admin table can have the
// same picker instead of reimplementing it. Nothing here is page-specific: the
// column set arrives as JSON on the `config` target, columns are addressed by
// `data-col-key`, and the localStorage key comes from `data-table-columns-key`.
//
// Preferences persist in localStorage (per browser/user), matching the existing
// admin sidebar-collapse pattern. Drag-to-reorder uses the native HTML5 Drag &
// Drop API (no extra dependency).
export default class extends Controller {
  static targets = ["panel", "list", "table", "config"]
  static values = { key: String }

  connect() {
    // A deferred page has no config yet at connect(); refresh() picks it up when
    // the fragment lands.
    if (!this.hasConfigTarget) {
      this._bindEvents()
      return
    }
    this.config = JSON.parse(this.configTarget.textContent)
    this.state = this._loadState()
    this._applyToTable()
    this._buildPanel()
    this._bindEvents()
  }

  _bindEvents() {
    this._onDocClick = this._handleOutsideClick.bind(this)
    document.addEventListener("click", this._onDocClick)

    // Tables that arrive after connect() — the Deal Funnel Dashboard defers its
    // controls row and results into a ?partial=true fragment, so at connect()
    // there is no table yet and each filter/sort/page swaps in a new one. The
    // fragment loader dispatches this event after a swap; re-applying is
    // idempotent, so it is safe for tables that were present all along.
    this._onTableReplaced = this.refresh.bind(this)
    this.element.addEventListener("table-columns:refresh", this._onTableReplaced)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
    this.element.removeEventListener("table-columns:refresh", this._onTableReplaced)
  }

  // Re-apply the saved order/visibility to whatever table is in the DOM now.
  //
  // Re-reads the config as well as the table: on the Deal Funnel Dashboard a
  // stage filter changes the column SET, not just the rows, so the fragment
  // brings a new config with it. _loadState keeps only keys present in the new
  // defaultOrder, so a column hidden while filtered to one stage stays hidden
  // when you return to it and is simply absent in between.
  refresh() {
    if (!this.hasConfigTarget) return
    this.config = JSON.parse(this.configTarget.textContent)
    this.state = this._loadState()
    this._applyToTable()
    this._buildPanel()
  }

  // --- State -----------------------------------------------------------

  get storageKey() {
    // Namespaced per table, so two tables on the system don't share prefs.
    return `table_columns:${this.keyValue || "default"}`
  }

  _loadState() {
    const fallback = {
      order: [...this.config.defaultOrder],
      hidden: [...this.config.defaultHidden]
    }
    try {
      const raw = localStorage.getItem(this.storageKey)
      if (!raw) return fallback
      const saved = JSON.parse(raw)
      const known = new Set(this.config.defaultOrder)
      // Keep only known keys, then append any new keys that appeared since.
      const order = (saved.order || []).filter((k) => known.has(k))
      this.config.defaultOrder.forEach((k) => { if (!order.includes(k)) order.push(k) })
      const locked = new Set(this.config.lockedKeys)
      const hidden = (saved.hidden || []).filter((k) => known.has(k) && !locked.has(k))
      return { order, hidden }
    } catch (e) {
      return fallback
    }
  }

  _saveState() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.state))
  }

  // --- Table application -----------------------------------------------

  _applyToTable() {
    if (!this.hasTableTarget) return
    const table = this.tableTarget
    // QAN-700: the LAST thead row, not the first. A table may carry a grouping
    // band above its labels (the Shelf Corp grid spans one over its seven
    // deliverable columns), and that band holds no data-col-key — picking the
    // first row there would find nothing to show, hide or reorder. For every
    // single-row thead, which is every other table using this picker, last and
    // first are the same element.
    const headRow = table.querySelector("thead tr:last-child")
    const bodyRows = table.querySelectorAll("tbody tr")

    this.state.order.forEach((key) => {
      const th = headRow.querySelector(`[data-col-key="${key}"]`)
      if (th) headRow.appendChild(th)
      bodyRows.forEach((row) => {
        const cell = row.querySelector(`[data-col-key="${key}"]`)
        if (cell) row.appendChild(cell)
      })
    })

    const hiddenSet = new Set(this.state.hidden)
    this.state.order.forEach((key) => {
      const visible = !hiddenSet.has(key)
      table.querySelectorAll(`[data-col-key="${key}"]`).forEach((el) => {
        el.classList.toggle("hidden", !visible)
      })
    })

    this._applyToBand(table, hiddenSet)
  }

  // A table may carry a grouping band above its labels — a row marked
  // `data-column-band` holding exactly three cells: a filler, the band itself
  // declaring the column keys it spans in `data-band-keys`, and a second filler.
  // The three-cell shape is the contract, not an incidental fact: the spans are
  // assigned by position relative to the band, so a second filler on one side
  // would be given that side's whole span a second time and push the row wider
  // than the table.
  //
  // The band's colspans cannot be rendered correctly server side, because this
  // controller hides columns with `display: none` (which removes them from the
  // row) and reorders them afterwards. Recompute from the order actually on
  // screen, every time.
  //
  // The span runs from the first visible band column to the last rather than
  // being their count, so a column dragged into the middle of the group widens
  // the band instead of leaving it pointing at the wrong cells. Tables with no
  // band row are untouched.
  _applyToBand(table, hiddenSet) {
    const bandRow = table.querySelector("thead tr[data-column-band]")
    if (!bandRow) return

    const visible = this.state.order.filter((key) => !hiddenSet.has(key))
    const cells = Array.from(bandRow.children)
    const bandCell = cells.find((cell) => (cell.dataset.bandKeys || "").length > 0)
    if (!bandCell) return

    const bandKeys = bandCell.dataset.bandKeys.split(",").filter(Boolean)
    const positions = bandKeys.map((key) => visible.indexOf(key)).filter((i) => i >= 0)

    // Every banded column hidden: collapse the band and let the filler cover
    // the table, rather than leaving a stripe over unrelated columns.
    if (positions.length === 0) {
      cells.forEach((cell, index) => {
        const leading = index === 0
        cell.colSpan = leading ? Math.max(visible.length, 1) : 1
        cell.classList.toggle("hidden", !leading)
      })
      return
    }

    const first = Math.min(...positions)
    const last = Math.max(...positions)
    const spans = [first, last - first + 1, visible.length - last - 1]
    const bandIndex = cells.indexOf(bandCell)

    cells.forEach((cell, index) => {
      // Order is [leading filler, band, trailing filler]; a zero-width filler is
      // hidden because a colspan of 0 renders as 1 in most browsers.
      const span = spans[index < bandIndex ? 0 : index === bandIndex ? 1 : 2]
      cell.colSpan = Math.max(span, 1)
      cell.classList.toggle("hidden", span <= 0)
    })
  }

  // --- Panel UI --------------------------------------------------------

  _buildPanel() {
    const locked = new Set(this.config.lockedKeys)
    const dynamic = new Set(this.config.dynamicKeys || [])
    const defaultHidden = new Set(this.config.defaultHidden)
    const hiddenSet = new Set(this.state.hidden)
    this.listTarget.innerHTML = ""

    // Group rows into the three mockup sections. Order within each group
    // follows the saved column order so drag-reordering is reflected.
    const always = []
    const visible = []
    const hidden = []
    this.state.order.forEach((key) => {
      if (locked.has(key)) always.push(key)
      else if (defaultHidden.has(key)) hidden.push(key)
      else visible.push(key)
    })

    this._appendSection("Always visible", always, { locked, dynamic, hiddenSet })
    this._appendSection("Visible by default", visible, { locked, dynamic, hiddenSet })
    this._appendSection("Hidden by default", hidden, { locked, dynamic, hiddenSet })
  }

  _appendSection(title, keys, ctx) {
    if (keys.length === 0) return
    const header = document.createElement("div")
    header.className = "text-xs font-semibold tracking-wider text-gray-400 uppercase px-1 pt-3 pb-1.5"
    header.textContent = title
    this.listTarget.appendChild(header)
    keys.forEach((key) => this.listTarget.appendChild(this._buildRow(key, ctx)))
  }

  _buildRow(key, { locked, dynamic, hiddenSet }) {
    const isLocked = locked.has(key)
    const isDynamic = dynamic.has(key)

    const row = document.createElement("div")
    row.className = "flex items-center gap-2 px-1 py-2 rounded hover:bg-gray-50"
    row.dataset.colKey = key

    const handle = document.createElement("span")
    handle.className = isLocked ? "text-gray-200 cursor-default" : "text-gray-400 cursor-grab"
    handle.textContent = "⋮⋮"
    // Only the handle is draggable so clicks on the checkbox/label are not
    // swallowed by the browser's drag initiation (Chrome quirk).
    handle.draggable = !isLocked

    const label = document.createElement("label")
    label.className = "flex items-center gap-2 flex-1 text-sm text-gray-700 cursor-pointer"

    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.className = "rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
    checkbox.checked = !hiddenSet.has(key)
    checkbox.disabled = isLocked
    checkbox.dataset.colKey = key
    checkbox.addEventListener("change", () => this._toggleColumn(key, checkbox.checked))

    const text = document.createElement("span")
    text.textContent = this.config.labels[key] || key
    if (isDynamic) {
      const note = document.createElement("span")
      note.className = "text-xs text-gray-400 ml-1"
      note.textContent = "(changes with filter)"
      text.appendChild(note)
    }

    label.appendChild(checkbox)
    label.appendChild(text)
    row.appendChild(handle)
    row.appendChild(label)
    row.appendChild(this._buildBadge(key, { isLocked, isDynamic }))

    if (!isLocked) this._wireDrag(row, handle)
    return row
  }

  _buildBadge(key, { isLocked, isDynamic }) {
    const badge = document.createElement("span")
    badge.className = "text-xs font-medium px-2 py-1 rounded-md"
    if (isDynamic) {
      badge.textContent = "dynamic"
      badge.classList.add("bg-indigo-50", "text-indigo-600")
    } else if (isLocked) {
      badge.textContent = "locked"
      badge.classList.add("bg-gray-100", "text-gray-500")
    } else if (this.config.defaultHidden.includes(key)) {
      badge.textContent = "hidden"
      badge.classList.add("bg-gray-100", "text-gray-400")
    } else {
      badge.className = ""
    }
    return badge
  }

  _wireDrag(row, handle) {
    handle.addEventListener("dragstart", (e) => {
      this._dragKey = row.dataset.colKey
      e.dataTransfer.effectAllowed = "move"
      row.classList.add("opacity-50")
    })
    handle.addEventListener("dragend", () => {
      row.classList.remove("opacity-50")
      this._dragKey = null
    })
    row.addEventListener("dragover", (e) => {
      e.preventDefault()
      const dragging = this.listTarget.querySelector(`[data-col-key="${this._dragKey}"]`)
      if (!dragging || dragging === row) return
      // Static sections: only reorder when both rows live in the same group.
      if (this._sectionOf(dragging) !== this._sectionOf(row)) return
      const rect = row.getBoundingClientRect()
      const after = e.clientY > rect.top + rect.height / 2
      this.listTarget.insertBefore(dragging, after ? row.nextSibling : row)
    })
    row.addEventListener("drop", (e) => {
      e.preventDefault()
      this._commitOrderFromPanel()
    })
  }

  // Which mockup section a column row belongs to: "always" | "visible" | "hidden".
  _sectionOf(row) {
    const key = row.dataset.colKey
    if (this.config.lockedKeys.includes(key)) return "always"
    if (this.config.defaultHidden.includes(key)) return "hidden"
    return "visible"
  }

  _commitOrderFromPanel() {
    // Only rows carry a data-col-key; section header divs are skipped so the
    // saved order never picks up undefined entries.
    this.state.order = Array.from(this.listTarget.querySelectorAll("[data-col-key]"))
      .map((row) => row.dataset.colKey)
    this._saveState()
    this._applyToTable()
    // Re-group rows back into their three sections after the move.
    this._buildPanel()
  }

  _toggleColumn(key, visible) {
    if (this.config.lockedKeys.includes(key)) return
    const hiddenSet = new Set(this.state.hidden)
    if (visible) hiddenSet.delete(key)
    else hiddenSet.add(key)
    this.state.hidden = Array.from(hiddenSet)
    this._saveState()
    this._applyToTable()
  }

  // --- Actions ---------------------------------------------------------

  togglePanel(event) {
    event.stopPropagation()
    this.panelTarget.classList.toggle("hidden")
  }

  reset(event) {
    event.preventDefault()
    localStorage.removeItem(this.storageKey)
    this.state = {
      order: [...this.config.defaultOrder],
      hidden: [...this.config.defaultHidden]
    }
    this._applyToTable()
    this._buildPanel()
  }

  _handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.panelTarget.classList.add("hidden")
    }
  }
}
